import { useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { CreatableOption, MuiSelectProps, OptionTypeWithCode } from '../../types';
import { isArray, isObject } from '../../../../utils/guards';
import { logger } from '../../../../utils/logger';
import { arrayUniqueByKey, filterNotEmpty } from '../../../../utils/listHelpers';
import { isCreatableOption } from './guards';

type Output<VALUE> = {
  list: OptionTypeWithCode<VALUE>[];
  selected: OptionTypeWithCode<VALUE>[];
};

/**
 * Provides menu items including selected ones to MuiSelect (mui Autocomplete).
 * If we provide only currently visible items without selected to MuiSelect (mui Autocomplete) - Autocomplete component will hide them
 * @param value
 * @param valueOptions
 * @param options
 * @param sort
 * @param showUnmatchedValues
 * @param showUnique
 */
export function useDropdownOptions<VALUE>(
  value: MuiSelectProps<boolean, VALUE>['value'],
  valueOptions: MuiSelectProps<boolean, VALUE>['valueOptions'],
  options: MuiSelectProps<boolean, VALUE>['options'],
  sort: NonNullable<MuiSelectProps['menuProps']>['sort'],
  showUnmatchedValues: NonNullable<MuiSelectProps['menuProps']>['showUnmatchedValues'],
  showUnique = true
): Output<VALUE> {
  const [selectedDropdownOptions, setSelectedDropdownOptions] = useState<OptionTypeWithCode<VALUE>[]>([]);

  useEffect(() => {
    const nextValues = toArray(value);
    const nextValueOptions = toArray(valueOptions);

    if (nextValueOptions.length) {
      /**
       * when valueOptions provided, compare them with previous one and decide which one to choose
       */
      setSelectedDropdownOptions((prevSelectedOptions) =>
        optionToString(prevSelectedOptions) !== optionToString(nextValueOptions)
          ? nextValueOptions
          : prevSelectedOptions
      );
      return;
    }

    setSelectedDropdownOptions((prevSelectedOptions) => {
      const nextSelectedOptions = filterNotEmpty(
        nextValues.map((value) => {
          const optionFromPrevSelectedOptions = prevSelectedOptions.find((it) => isEqual(it.value, value));
          const optionFromNextOptions = options.find((it) => isEqual(it.value, value));

          return selectMostValuableOption(
            optionFromNextOptions,
            optionFromPrevSelectedOptions,
            showUnmatchedValues ? ({ label: getLabel(value), value } as OptionTypeWithCode<VALUE>) : undefined
          );
        })
      );

      return optionToString(prevSelectedOptions) !== optionToString(nextSelectedOptions)
        ? nextSelectedOptions
        : prevSelectedOptions;
    });
  }, [options, value, valueOptions, showUnmatchedValues]);

  const dropdownOptions = useMemo(() => {
    const dropdownOptions = options || [];
    const targetOptions = [
      ...(sort ? [...dropdownOptions].sort(compareOptions) : dropdownOptions),
      ...selectedDropdownOptions
    ];
    return showUnique ? arrayUniqueByKey(targetOptions, 'value') : targetOptions;
  }, [options, selectedDropdownOptions, sort, showUnique]);

  return {
    list: dropdownOptions,
    selected: selectedDropdownOptions
  };
}

/**
 * @function omitNonStringValues
 *
 * @description
 * Removes non-string values from the given options object or array.
 * Returns a new object or array with only the string values.
 *
 * @param {OptionTypeWithCode<VALUE> | OptionTypeWithCode<VALUE>[] | undefined} options - The options object or array to filter.
 *
 * @return {OptionTypeWithCode<VALUE> | OptionTypeWithCode<VALUE>[] | undefined} - The filtered options object or array.
 */
export function omitNonStringValues<
  VALUE extends unknown,
  T extends OptionTypeWithCode<VALUE> | OptionTypeWithCode<VALUE>[] | undefined
>(options: T): T | undefined {
  if (!options) {
    return undefined;
  }
  if (isArray(options)) {
    return options.map((option) => {
      const { icon, subLabel, ...rest } = option;
      return rest;
    }) as T;
  }
  const { icon, subLabel, ...rest } = options as OptionTypeWithCode<VALUE>;
  return rest as T;
}

function optionToString(options: OptionTypeWithCode | OptionTypeWithCode[] | undefined | null): string {
  try {
    return options ? JSON.stringify(omitNonStringValues(options)) : '';
  } catch (e) {
    logger.warn(e);
    return '';
  }
}

/**
 * Prefers options where label is different from value (effect of 'showUnmatchedValues' prop).
 * @param option1
 * @param option2
 * @param fallbackOption
 */
function selectMostValuableOption<VALUE>(
  option1: OptionTypeWithCode<VALUE> | undefined,
  option2: OptionTypeWithCode<VALUE> | undefined,
  fallbackOption: OptionTypeWithCode<VALUE> | undefined
): OptionTypeWithCode<VALUE> | undefined {
  if (option1 && option1.value !== option1.label) {
    return option1;
  }
  if (option2 && option2.value !== option2.label) {
    return option2;
  }
  return option1 ?? option2 ?? fallbackOption;
}

function toArray<V>(value: V | V[] | null | undefined): V[] {
  return isArray(value) ? value : ([value].filter((it) => typeof it !== 'undefined' && it !== null) as V[]);
}

function compareOptions<VALUE extends unknown>(
  a: OptionTypeWithCode<VALUE> | CreatableOption,
  b: OptionTypeWithCode<VALUE> | CreatableOption
) {
  if (isCreatableOption(a)) {
    return 1;
  }
  if (isCreatableOption(b)) {
    return -1;
  }
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }
  return 0;
}

function getLabel(value: unknown): string {
  try {
    if (typeof value === 'string') {
      return value;
    }
    if (isObject(value) && 'label' in value) {
      return `${value.label}`;
    }
    return '';
  } catch {
    return '';
  }
}
